import { orgAPI } from "@/api/api";
import { useUpdateCanvas } from "@/api/canvas";
import {
  fileRecordKeys,
  useCopyFileRecord,
  useDeleteFileRecord,
  useMoveFileRecord,
} from "@/api/fileRecords";
import { useUpdateWorkspaceInboxItem } from "@/api/flow_sessions";
import { useSetCanvasFolder, useSetFlowSessionFolder } from "@/api/folders";
import { useToggleFavorite } from "@/api/resourceTags";
import {
  useFileConflictResolver,
  type IntendedCopy,
  type IntendedMove,
  type ResolvedCopyOperation,
  type ResolvedOperation,
} from "@/components/FileConflictResolver";
import { moveRecordCascading } from "@/pages/workspaces/utils/folderMove";
import type { FileRecord, FileRecordComponents } from "@cohesive/api-client";
import type { DragCancelEvent, DragEndEvent } from "@dnd-kit/core";
import { useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { useCallback, useEffect, useRef } from "react";
import toast from "react-hot-toast";
import type {
  CanvasBoardDropHandler,
  CanvasReparentHandler,
  ChatAttachHandler,
  ComposerAttachTarget,
  WorkspaceDetailDropContext,
} from "./TransferDndProvider";
import { buildWorkspaceMoves, partitionRailFilePayloads } from "./buildIntents";
import { classifyTransfer } from "./classifyTransfer";
import {
  buildWorkspaceDetailDraggable,
  classifyMixedWorkspaceDetailDrop,
  isMixedWorkspaceDetailSelection,
  runWorkspaceDetailMixedDrop,
} from "./handlers/workspaceDetailDrop";
import { asDraggableData, asDroppableData, type DraggableData } from "./ids";
import type { FileRecordCopyPayload, TransferDestination } from "./types";

/**
 * One-stop dispatcher for every in-app transfer drop.
 */
export function useTransferDispatcher(opts: {
  getBoardDropHandler: () => CanvasBoardDropHandler | null;
  getReparentHandler: () => CanvasReparentHandler | null;
  getChatAttachHandler: () => ChatAttachHandler | null;
  getComposerAttachTarget: () => ComposerAttachTarget | null;
  getWorkspaceDetailContext: () => WorkspaceDetailDropContext | null;
}) {
  const getBoardDropHandlerRef = useRef(opts.getBoardDropHandler);
  getBoardDropHandlerRef.current = opts.getBoardDropHandler;
  const getReparentHandlerRef = useRef(opts.getReparentHandler);
  getReparentHandlerRef.current = opts.getReparentHandler;
  const getChatAttachHandlerRef = useRef(opts.getChatAttachHandler);
  getChatAttachHandlerRef.current = opts.getChatAttachHandler;
  const getComposerAttachTargetRef = useRef(opts.getComposerAttachTarget);
  getComposerAttachTargetRef.current = opts.getComposerAttachTarget;
  const getWorkspaceDetailContextRef = useRef(opts.getWorkspaceDetailContext);
  getWorkspaceDetailContextRef.current = opts.getWorkspaceDetailContext;

  // dnd-kit's `delta` in DragEndEvent accumulates scroll adjustments from
  // its auto-scroller (e.g. when the pointer is near the entity selector's
  // bottom edge during drag activation, it auto-scrolls the selector and
  // subtracts that scroll from delta.y). This corrupts `activatorEvent +
  // delta` as a proxy for the final cursor position. Track the raw pointer
  // position ourselves so onDragEnd always has the true viewport coords.
  const lastPointerRef = useRef({ x: 0, y: 0 });
  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      lastPointerRef.current = { x: e.clientX, y: e.clientY };
    };
    // CAPTURE phase: runs before dnd-kit's own pointer handlers, so a
    // `stopPropagation()` in the sensor can't starve us of moves mid-drag —
    // which was leaving the ref frozen at the drag's start (over the left rail)
    // and dropping nodes at the canvas origin. `pointerdown`/`dragover` seed and
    // keep it fresh for touch + native drags too.
    const opts = { passive: true, capture: true } as const;
    document.addEventListener("pointermove", onMove, opts);
    document.addEventListener("pointerdown", onMove, opts);
    document.addEventListener("dragover", onMove as EventListener, opts);
    return () => {
      document.removeEventListener("pointermove", onMove, opts);
      document.removeEventListener("pointerdown", onMove, opts);
      document.removeEventListener("dragover", onMove as EventListener, opts);
    };
  }, []);

  /**
   * The drop's true viewport position. Prefers the live-tracked pointer; if that
   * was never updated (still the `(0,0)` init), falls back to
   * `activatorEvent + delta` so a drop can never silently land at the corner.
   */
  const resolveDropPoint = (event: DragEndEvent) => {
    const p = lastPointerRef.current;
    if (p.x !== 0 || p.y !== 0) return p;
    const act = event.activatorEvent as Partial<PointerEvent> | null;
    if (
      act &&
      typeof act.clientX === "number" &&
      typeof act.clientY === "number"
    ) {
      return { x: act.clientX + event.delta.x, y: act.clientY + event.delta.y };
    }
    return p;
  };

  const queryClient = useQueryClient();
  const { resolveCopies, resolveMoves } = useFileConflictResolver();
  const copyFileRecord = useCopyFileRecord(undefined);
  // Move/delete mutations are workspace-scoped for invalidation. We
  // bind them with `undefined` here and rely on the underlying mutation
  // body to derive the workspace id from the payload.
  const moveFileRecord = useMoveFileRecord(undefined);
  const deleteFileRecord = useDeleteFileRecord(undefined);
  const updateCanvas = useUpdateCanvas();
  const toggleFavorite = useToggleFavorite();
  const setCanvasFolder = useSetCanvasFolder();
  const setFlowSessionFolder = useSetFlowSessionFolder();
  const updateInboxItem = useUpdateWorkspaceInboxItem();

  const runCopies = useCallback(
    async (payloads: FileRecordCopyPayload[], dest: TransferDestination) => {
      // Favorites, canvas/chat folders, and the pending composer are not
      // copy destinations here (all handled upstream — the composer via
      // `attachFilesToSession`); guard so the canvas branch stays well-typed.
      if (dest.kind === "favorites") return;
      if (dest.kind === "canvas_folder") return;
      if (dest.kind === "chat_folder") return;
      if (dest.kind === "composer") return;
      const components: Omit<FileRecordComponents, "path" | "is_folder"> =
        dest.kind === "workspace"
          ? { class: "workspace", workspace_id: dest.workspaceId }
          : dest.kind === "flow_session"
            ? {
                class: "flow-session",
                flow_session_id: dest.flowSessionId,
              }
            : { class: "canvas", canvas_id: dest.canvasId };
      const parentPath =
        dest.kind === "workspace"
          ? dest.parentPath
          : dest.kind === "flow_session"
            ? ""
            : "";

      const copies: IntendedCopy[] = payloads.map((p) => ({
        fileRecordId: p.recordId,
        sourceName: p.name,
        isFolder: p.isFolder,
        source: {
          class: p.sourceClass,
          workspace_id: p.sourceWorkspaceId,
          canvas_id: p.sourceCanvasId,
          flow_session_id: p.sourceFlowSessionId,
          path: p.sourcePath,
          is_folder: p.isFolder || undefined,
        },
        destination: { components, parentPath },
      }));

      const sourceRecords = await collectCopySourceRecords(
        queryClient,
        payloads,
      );

      const ops = await resolveCopies({ copies, sourceRecords });
      if (ops === null) return;
      if (ops.length === 0) return;

      let successes = 0;
      let failures = 0;
      for (const op of ops) {
        try {
          await runCopyOperation(op, copyFileRecord.mutateAsync);
          successes += 1;
        } catch {
          failures += 1;
        }
      }

      const targetLabel =
        dest.kind === "workspace"
          ? dest.parentPath
            ? "folder"
            : "workspace"
          : dest.kind === "flow_session"
            ? "chat"
            : "canvas";
      reportBatchResult({
        verb: "Copied",
        successes,
        failures,
        total: ops.length,
        targetLabel,
      });
    },
    [queryClient, resolveCopies, copyFileRecord.mutateAsync],
  );

  const runMoves = useCallback(
    async (
      payloads: FileRecordCopyPayload[],
      dest: Extract<TransferDestination, { kind: "workspace" }>,
    ) => {
      const { moves, rejections } = buildWorkspaceMoves(payloads, {
        workspaceId: dest.workspaceId,
        parentPath: dest.parentPath,
      });

      if (rejections.some((r) => r.kind === "self_folder")) {
        toast.error("Cannot move a folder into itself");
        return;
      }

      // Nothing to do — every payload was a same-parent no-op or out
      // of scope. Stay quiet rather than toasting "moved 0 items".
      if (moves.length === 0) return;

      const sourceRecords = await collectWorkspaceSourceRecords(
        queryClient,
        payloads,
      );
      const sourceRecordMap = new Map(sourceRecords.map((r) => [r.id, r]));

      const intendedMoves: IntendedMove[] = moves.map((m) => ({
        fileRecordId: m.recordId,
        sourceName: m.sourceName,
        destinationParentPath: m.destinationParentPath,
        isFolder: m.isFolder,
      }));

      const ops = await resolveMoves({
        moves: intendedMoves,
        sourceRecords,
        destinationWorkspaceId: dest.workspaceId,
      });
      if (ops === null) return;
      if (ops.length === 0) return;

      let successes = 0;
      let failures = 0;
      const moveOne = async (vars: {
        fileRecordId: string;
        payload: import("@cohesive/api-client").MoveFileRecordPayload;
      }) => {
        const sourceRecord = sourceRecordMap.get(vars.fileRecordId);
        const moved = await moveFileRecord.mutateAsync(vars);
        patchWorkspaceMoveCaches(
          queryClient,
          sourceRecord?.workspace_id,
          moved,
        );
        return moved;
      };

      for (const op of ops) {
        try {
          await runMoveOperation(
            op,
            sourceRecordMap,
            sourceRecords,
            moveOne,
            copyFileRecord.mutateAsync,
            deleteFileRecord.mutateAsync,
          );
          successes += 1;
        } catch (err) {
          console.error("[TransferDrop] move op failed", op, err);
          failures += 1;
        }
      }

      invalidateWorkspaceListCaches(queryClient, payloads, dest.workspaceId);

      const targetLabel = dest.parentPath ? "folder" : "workspace";
      reportBatchResult({
        verb: "Moved",
        successes,
        failures,
        total: ops.length,
        targetLabel,
      });
    },
    [
      queryClient,
      resolveMoves,
      moveFileRecord.mutateAsync,
      copyFileRecord.mutateAsync,
      deleteFileRecord.mutateAsync,
    ],
  );

  // Reparent batch: hand off to the canvas page's window-event handler
  // which owns React Flow / Loro coordinates and the auto-slot
  // geometry for inserting children into a target section. The
  // dispatcher here stays storage-agnostic — it just describes the
  // intent, the canvas page applies it.
  const runReparent = useCallback(
    (
      canvasId: string,
      nodeIds: string[],
      targetParentSectionId: string | null,
    ) => {
      if (nodeIds.length === 0) return;
      const handler = getReparentHandlerRef.current();
      if (handler) {
        void Promise.resolve(
          handler({ canvasId, nodeIds, targetParentSectionId }),
        ).catch((err) => {
          console.error("[TransferDrop] reparent handler failed", err);
          toast.error("Failed to move items");
        });
        return;
      }
      window.dispatchEvent(
        new CustomEvent("canvas:reparent-node", {
          detail: { canvasId, nodeIds, targetParentSectionId },
        }),
      );
    },
    [],
  );

  const runMoveCanvases = useCallback(
    async (
      canvasIds: string[],
      workspaceId: string,
      canvasMap: Map<string, import("@cohesive/api-client").Canvas>,
    ) => {
      const promises: Promise<void>[] = [];
      for (const cId of canvasIds) {
        const canvas = await resolveCanvasForMove(queryClient, cId, canvasMap);
        if (!canvas) continue;
        // Always perform the move. The caller only routes here once it has
        // decided this is a cross-workspace move (sourceWorkspaceId !== dest),
        // so we must not second-guess it against a possibly-stale cached
        // workspace_id — doing so silently dead-ended real moves ("can't move
        // until refresh"). Re-setting the same workspace is a harmless no-op
        // server-side and the refetch reconciles any stale list.
        promises.push(
          updateCanvas
            .mutateAsync({
              id: cId,
              data: {
                name: canvas.name,
                description: canvas.description,
                metadata: canvas.metadata,
                workspace_id: workspaceId,
              },
            })
            .then(() => undefined),
        );
      }
      if (promises.length === 0) return;
      const results = await Promise.allSettled(promises);
      const failures = results.filter((r) => r.status === "rejected").length;
      const successes = promises.length - failures;
      if (failures === 0) {
        toast.success(
          successes === 1
            ? "Moved 1 canvas to workspace"
            : `Moved ${successes} canvases to workspace`,
        );
      } else if (successes === 0) {
        toast.error("Failed to move canvas");
      } else {
        toast.error(`Moved ${successes} of ${promises.length} canvases`);
      }
    },
    [queryClient, updateCanvas],
  );

  const runSectionToFolder = useCallback(
    async (
      payload: import("./payloads").ExplorerSectionPayload,
      dest: Extract<TransferDestination, { kind: "workspace" }>,
    ) => {
      // Defensive — classifier already short-circuits, but a future
      // caller routing a section drop directly here shouldn't crash.
      if (payload.files.length === 0) {
        toast("Nothing to copy; this section has no files.");
        return;
      }

      const folderName = payload.sectionName.trim() || "Section";
      const folderPath = `${dest.parentPath}${folderName}/`;

      // Mint the folder marker. Reuses the exact call shape from
      // `useWorkspaceUploadQueue.ts`'s `enqueueTree`. On 409 / duplicate
      // (folder already exists at that name) we silently merge — the
      // per-file `resolveCopies` step below handles per-file collisions
      // inside the folder via the standard Replace / Keep both / Skip
      // dialog.
      let folderRecord: FileRecord | null = null;
      try {
        folderRecord = await orgAPI.fileRecords.create({
          provider: "s3",
          destination: {
            class: "workspace",
            workspace_id: dest.workspaceId,
            path: folderPath,
            is_folder: true,
          },
        });
      } catch (err) {
        const status = (err as AxiosError | undefined)?.response?.status;
        if (status !== 409) {
          console.error("[ExplorerDrop] section folder create failed", err);
          toast.error(`Failed to create folder "${folderName}"`);
          return;
        }
        // 409: folder already exists, proceed to merge.
      }

      // Patch the workspace cache so the new folder appears immediately
      // (mirrors `useWorkspaceUploadQueue.ts`'s `patchWorkspaceList`).
      const workspaceKey = fileRecordKeys.workspace(dest.workspaceId);
      if (folderRecord) {
        const created = folderRecord;
        queryClient.setQueryData(fileRecordKeys.detail(created.id), created);
        queryClient.setQueryData<FileRecord[]>(workspaceKey, (prev) => {
          if (!prev) return prev;
          const idx = prev.findIndex((r) => r.id === created.id);
          if (idx === -1) return [...prev, created];
          const next = prev.slice();
          next[idx] = { ...prev[idx], ...created };
          return next;
        });
      }

      // Run the per-file copy through the existing cross-scope pipeline
      // — same conflict resolver UX users already know.
      const copies: IntendedCopy[] = payload.files.map((p) => ({
        fileRecordId: p.recordId,
        sourceName: p.name,
        isFolder: p.isFolder,
        source: {
          class: p.sourceClass,
          workspace_id: p.sourceWorkspaceId,
          canvas_id: p.sourceCanvasId,
          flow_session_id: p.sourceFlowSessionId,
          path: p.sourcePath,
          is_folder: p.isFolder || undefined,
        },
        destination: {
          components: { class: "workspace", workspace_id: dest.workspaceId },
          parentPath: folderPath,
        },
      }));

      const ops = await resolveCopies({ copies, sourceRecords: [] });
      if (ops === null) return;
      if (ops.length === 0) return;

      let successes = 0;
      let failures = 0;
      for (const op of ops) {
        try {
          await runCopyOperation(op, copyFileRecord.mutateAsync);
          successes += 1;
        } catch {
          failures += 1;
        }
      }

      if (failures === 0) {
        toast.success(
          successes === 1
            ? `Copied 1 file into "${folderName}"`
            : `Copied ${successes} files into "${folderName}"`,
        );
      } else if (successes === 0) {
        toast.error(`Failed to copy section into "${folderName}"`);
      } else {
        toast.error(
          `Copied ${successes} of ${ops.length} files into "${folderName}", ${failures} failed`,
        );
      }
    },
    [queryClient, resolveCopies, copyFileRecord.mutateAsync],
  );

  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over) return;

      /** Copy onto the open canvas board (copy record + mint node at cursor). */
      const invokeCanvasBoardFileDrop = async (
        payloads: FileRecordCopyPayload[],
        opts?: {
          useViewportPlacement?: boolean;
          parentSectionId?: string;
        },
      ): Promise<boolean> => {
        const handler = getBoardDropHandlerRef.current();
        if (!handler) return false;
        try {
          const point = resolveDropPoint(event);
          const accepted = await handler({
            screenX: point.x,
            screenY: point.y,
            payloads,
            ...(opts?.parentSectionId
              ? { parentSectionId: opts.parentSectionId }
              : {}),
            ...(opts?.useViewportPlacement
              ? { useViewportPlacement: true }
              : {}),
          });
          return accepted !== false;
        } catch (err) {
          console.error("[ExplorerDrop] canvas-board handler failed", err);
          toast.error("Failed to drop onto canvas");
          return false;
        }
      };

      const droppable = asDroppableData(over.data.current);
      if (!droppable) return;

      let draggable = asDraggableData(active.data.current);
      const selectionSnapshot = active.data.current?.selectionSnapshot as
        | string[]
        | undefined;
      const detailCtx = getWorkspaceDetailContextRef.current();

      // Mixed workspace-detail selection (files + canvases) only carries
      // `selectionSnapshot` — buildWorkspaceDetailDraggable returns null.
      if (
        selectionSnapshot &&
        detailCtx &&
        isMixedWorkspaceDetailSelection(selectionSnapshot)
      ) {
        const mixedClassification = classifyMixedWorkspaceDetailDrop(
          droppable,
          selectionSnapshot,
        );
        if (mixedClassification.kind === "invalid") {
          toast.error(mixedClassification.reason);
          return;
        }

        const destination = droppable.destination;
        if (destination.kind !== "workspace") return;
        const destWorkspaceId = destination.workspaceId;
        const targetLabel = mixedClassification.targetLabel;
        void runWorkspaceDetailMixedDrop({
          selectionIds: selectionSnapshot,
          destination,
          sourceWorkspaceId: detailCtx.sourceWorkspaceId,
          canvasMap: detailCtx.canvasMap,
          fileRecordMap: detailCtx.fileRecordMap,
          updateCanvas: (vars) => updateCanvas.mutateAsync(vars),
          moveFileRecord: (vars) => moveFileRecord.mutateAsync(vars),
          copyFileRecord: (vars) => copyFileRecord.mutateAsync(vars),
          deleteFileRecord: (id) => deleteFileRecord.mutateAsync(id),
          resolveMoves,
          targetLabel,
        })
          .then(() => {
            void queryClient.invalidateQueries({
              queryKey: fileRecordKeys.workspace(detailCtx.sourceWorkspaceId),
            });
            if (detailCtx.sourceWorkspaceId !== destWorkspaceId) {
              void queryClient.invalidateQueries({
                queryKey: fileRecordKeys.workspace(destWorkspaceId),
              });
            }
          })
          .catch((err) => {
            console.error("[TransferDrop] mixed workspace drop failed", err);
            toast.error(`Failed to move to ${targetLabel}`);
          });
        return;
      }

      if (!draggable && selectionSnapshot && detailCtx) {
        draggable = buildWorkspaceDetailDraggable(
          selectionSnapshot,
          detailCtx.sourceWorkspaceId,
          detailCtx.canvasMap,
          detailCtx.fileRecordMap,
          detailCtx.inboxItemMap,
        );
      }
      if (!draggable) return;

      // Canvas folder in the left rail: dropping a canvas card re-files it
      // (a move). Handled here, ahead of the generic classifier.
      if (
        droppable.surface === "rail-canvas-folder" &&
        draggable.kind === "canvas-card"
      ) {
        if (droppable.destination.kind !== "canvas_folder") return;
        const dest = droppable.destination;
        // Filing requires the canvas to live in the folder's workspace, so an
        // unknown source workspace (legacy personal canvas, `workspace_id`
        // null) is NOT same-workspace — it takes the cross-workspace path
        // below, which resolves the canvas and moves it in before filing.
        const sameWorkspace =
          !!draggable.sourceWorkspaceId &&
          draggable.sourceWorkspaceId === dest.workspaceId;
        // Redundant drop (already in this folder / already at root): the pill
        // shows a live "Move" so the drag reads as valid, but the drop itself is
        // a silent no-op — skip the write when nothing changes. Strict equality
        // on purpose: `undefined` provenance (multi-canvas drags) must NOT
        // match a root target — those run the writes so filed canvases in the
        // selection still move.
        if (sameWorkspace && draggable.sourceFolderId === dest.folderId) {
          return;
        }
        if (sameWorkspace) {
          // File into the folder, or to the workspace root (folderId null =
          // un-folder). Folder membership is shared state set via setCanvasFolder.
          for (const canvasId of draggable.canvasIds) {
            setCanvasFolder.mutate({
              canvasId,
              folderId: dest.folderId,
              workspaceId: dest.workspaceId,
            });
          }
        } else {
          // Cross-workspace: move to the destination workspace first (the
          // backend clears folder_id on a workspace change, since folders are
          // workspace-scoped), then — if dropped on a specific folder — file it
          // there. The folder is only valid once the canvas lives in its
          // workspace, so the two ops must be sequential.
          const ctx = getWorkspaceDetailContextRef.current();
          const canvasMap =
            ctx?.canvasMap ??
            new Map<string, import("@cohesive/api-client").Canvas>();
          const targetFolderId = dest.folderId;
          const targetWorkspaceId = dest.workspaceId;
          const canvasIds = draggable.canvasIds;
          void (async () => {
            await runMoveCanvases(canvasIds, targetWorkspaceId, canvasMap);
            if (targetFolderId) {
              for (const canvasId of canvasIds) {
                await setCanvasFolder.mutateAsync({
                  canvasId,
                  folderId: targetFolderId,
                  workspaceId: targetWorkspaceId,
                });
              }
            }
          })().catch((err) => {
            console.error(
              "[TransferDrop] move canvas to workspace failed",
              err,
            );
            toast.error("Failed to move canvas");
          });
        }
        return;
      }

      // Chat folder in the left rail: dropping a chat-session re-files it (a
      // move), or un-folders it when folderId is null. Same-workspace only —
      // chat folders are workspace-scoped and chats don't change workspace here.
      if (
        droppable.surface === "rail-chat-folder" &&
        draggable.kind === "chat-session"
      ) {
        if (droppable.destination.kind !== "chat_folder") return;
        const dest = droppable.destination;
        // Chats never change workspace by filing, so unknown provenance fails
        // closed rather than filing into a possibly-foreign workspace's folder.
        const src = draggable.payload.sourceWorkspaceId;
        if (!src || src !== dest.workspaceId) return;
        if ((draggable.payload.sourceFolderId ?? null) === dest.folderId) {
          return;
        }
        setFlowSessionFolder.mutate({
          flowSessionId: draggable.payload.flowSessionId,
          folderId: dest.folderId,
          workspaceId: dest.workspaceId,
        });
        return;
      }

      // Canvas dropped on its own workspace's root surface while filed in a
      // folder — or with unknown provenance (multi-canvas drags) — un-folder it
      // (clear folder_id; idempotent for canvases already at the root). Only a
      // known-root drag (`sourceFolderId === null`) skips. Cross-workspace
      // drops fall through to the move_canvas classification below (which also
      // clears it).
      if (
        droppable.surface === "workspace-folder" &&
        draggable.kind === "canvas-card" &&
        droppable.destination.kind === "workspace" &&
        droppable.destination.parentPath === "" &&
        draggable.sourceWorkspaceId === droppable.destination.workspaceId &&
        draggable.sourceFolderId !== null
      ) {
        for (const canvasId of draggable.canvasIds) {
          setCanvasFolder.mutate({
            canvasId,
            folderId: null,
            workspaceId: droppable.destination.workspaceId,
          });
        }
        return;
      }

      const classification = classifyTransfer(draggable, droppable.destination);

      // Files rail drop into a workspace folder. Shared-with-me files always
      // COPY — the recipient can't relocate a file out of the owner's scope
      // without stripping the owner's parent-derived access (the backend
      // enforces this too). Owned files follow the normal partition:
      // workspace-class files move (incl. cross-workspace), cross-class
      // (canvas / flow-session) files copy.
      if (
        droppable.surface === "rail-workspace-folder" &&
        draggable.kind === "files"
      ) {
        if (droppable.destination.kind !== "workspace") return;
        const dest = droppable.destination;
        const sharedPayloads = draggable.payloads.filter((p) => p.sharedSource);
        const ownedPayloads = draggable.payloads.filter((p) => !p.sharedSource);
        const { workspace, crossClassCopy } =
          partitionRailFilePayloads(ownedPayloads);
        const tasks: Promise<void>[] = [];
        if (workspace.length > 0) {
          tasks.push(runMoves(workspace, dest));
        }
        if (crossClassCopy.length > 0) {
          tasks.push(runCopies(crossClassCopy, dest));
        }
        if (sharedPayloads.length > 0) {
          tasks.push(runCopies(sharedPayloads, dest));
        }
        if (tasks.length === 0) return;
        void Promise.all(tasks).catch((err) => {
          console.error("[TransferDrop] rail drop dispatch failed", err);
          toast.error("Failed to drop items");
        });
        return;
      }

      // Copy the dragged records into the session (chat attachments are
      // session-class copies), then hand the created records to `onAttached`
      // so the composer's attachment queue picks them up. Shared by the chat
      // page's `chat-composer` surface and the home/workspace composer's
      // `composer-attach` surface below.
      const attachFilesToSession = async (
        flowSessionId: string,
        files: Extract<DraggableData, { kind: "files" }>,
        onAttached: ChatAttachHandler | null,
      ) => {
        const components = {
          class: "flow-session" as const,
          flow_session_id: flowSessionId,
        };
        const copies: IntendedCopy[] = files.payloads.map((p) => ({
          fileRecordId: p.recordId,
          sourceName: p.name,
          isFolder: p.isFolder,
          source: {
            class: p.sourceClass,
            workspace_id: p.sourceWorkspaceId,
            canvas_id: p.sourceCanvasId,
            flow_session_id: p.sourceFlowSessionId,
            path: p.sourcePath,
            is_folder: p.isFolder || undefined,
          },
          destination: { components, parentPath: "" },
        }));
        const sourceRecords = await collectCopySourceRecords(
          queryClient,
          files.payloads,
        );
        const ops = await resolveCopies({ copies, sourceRecords });
        if (ops === null || ops.length === 0) return;
        const created: FileRecord[] = [];
        for (const op of ops) {
          try {
            const record = await copyFileRecord.mutateAsync({
              fileRecordId: op.sourceFileRecordId,
              payload: op.payload,
            });
            if (record) created.push(record);
          } catch {
            toast.error("Failed to attach file");
          }
        }
        if (onAttached && created.length > 0) {
          await onAttached(created);
        }
        if (created.length > 0) {
          toast.success(
            created.length === 1
              ? "Attached 1 file"
              : `Attached ${created.length} files`,
          );
        }
      };

      if (
        droppable.surface === "chat-composer" &&
        classification.kind === "copy" &&
        draggable.kind === "files"
      ) {
        const dest = droppable.destination;
        if (dest.kind !== "flow_session") return;
        void attachFilesToSession(
          dest.flowSessionId,
          draggable,
          getChatAttachHandlerRef.current(),
        ).catch((err) => {
          console.error("[TransferDrop] chat attach failed", err);
          toast.error("Failed to attach files");
        });
        return;
      }

      // Home/workspace composer: the chat may not exist yet. Ask the
      // composer to mint (or return) its flow session — the same auto-create
      // its attach button performs — then run the identical copy+attach.
      if (
        droppable.surface === "composer-attach" &&
        classification.kind === "copy" &&
        draggable.kind === "files"
      ) {
        const target = getComposerAttachTargetRef.current();
        if (!target) return;
        void (async () => {
          const flowSessionId = await target.ensureSession();
          if (!flowSessionId) return;
          await attachFilesToSession(
            flowSessionId,
            draggable,
            target.onAttached,
          );
        })().catch((err) => {
          console.error("[TransferDrop] composer attach failed", err);
          toast.error("Failed to attach files");
        });
        return;
      }

      // Rail Favorites section: star each dropped row (same effect as
      // the row's Star menu item). The classifier already filtered to
      // favoritable items for this rail's resource type.
      if (droppable.surface === "rail-favorites") {
        if (classification.kind !== "favorite") return;
        for (const item of classification.items) {
          if (item.via === "resource-tag") {
            toggleFavorite.mutate({
              resourceType: item.resourceType,
              resourceId: item.resourceId,
              isFavorite: false,
            });
          } else {
            updateInboxItem.mutate({
              id: item.inboxItemId,
              is_starred: true,
            });
          }
        }
        return;
      }

      // Canvas explorer scope (canvas rail body or Files-rail context
      // section): the pointer sits over the sidebar, so placement uses
      // the visible viewport — not screen coords on the board.
      if (
        droppable.surface === "canvas-scope" ||
        droppable.surface === "files-rail-canvas-scope"
      ) {
        if (
          classification.kind === "noop" ||
          classification.kind === "invalid"
        ) {
          return;
        }
        if (draggable.kind === "entity" || draggable.kind === "skill") {
          const handler = getBoardDropHandlerRef.current();
          if (!handler) return;
          void Promise.resolve(
            handler({
              screenX: 0,
              screenY: 0,
              payloads: [],
              entities: draggable.entities,
              useViewportPlacement: true,
            }),
          ).catch((err) => {
            console.error(
              "[ExplorerDrop] canvas-scope entity drop failed",
              err,
            );
            toast.error("Failed to place entity on canvas");
          });
          return;
        }
        if (draggable.kind !== "files") return;
        if (classification.kind === "reparent_node") {
          runReparent(
            classification.canvasId,
            classification.nodeIds,
            classification.targetParentSectionId,
          );
          return;
        }
        if (classification.kind === "place" || classification.kind === "copy") {
          void (async () => {
            const placed = await invokeCanvasBoardFileDrop(draggable.payloads, {
              useViewportPlacement: true,
              ...(classification.kind === "place" &&
              classification.parentSectionId
                ? { parentSectionId: classification.parentSectionId }
                : {}),
            });
            if (placed) return;
            if (classification.kind === "copy") {
              void runCopies(draggable.payloads, droppable.destination).catch(
                (err) => {
                  console.error("[ExplorerDrop] copy dispatch failed", err);
                  toast.error("Failed to copy items");
                },
              );
            }
          })();
          return;
        }
        return;
      }

      // Rail canvas row for a canvas that ISN'T open: there's no board mounted
      // to place a node on, so a dropped file just copies into that canvas's
      // file scope (it appears as a node-less file you place when you open the
      // canvas). When the canvas IS open the row uses the `canvas-scope` surface
      // above, which places a node via the board handler.
      if (droppable.surface === "rail-canvas-row") {
        if (draggable.kind !== "files") return;
        if (
          classification.kind === "noop" ||
          classification.kind === "invalid"
        ) {
          return;
        }
        void runCopies(draggable.payloads, droppable.destination).catch(
          (err) => {
            console.error("[TransferDrop] rail canvas row copy failed", err);
            toast.error("Failed to add file to canvas");
          },
        );
        return;
      }

      // Canvas-board: hand off to the registered board handler so
      // CanvasPage can run its `screenToFlowPosition` + `addNode`
      // pipeline against the cursor's screen coordinates at drop.
      // Section drops onto a canvas board are no-ops by classifier
      // rule (handled below) — we forward file payloads, entity
      // payloads, and the chat-session placement variant to the board
      // handler.
      if (droppable.surface === "canvas-board") {
        // Use the real-time tracked pointer position rather than
        // `activatorEvent + delta`. dnd-kit's delta accumulates
        // auto-scroll adjustments (e.g. the selector auto-scrolls while
        // the pointer is near its edge), which shifts the computed drop
        // position away from where the user actually released.
        const { x: screenX, y: screenY } = resolveDropPoint(event);

        // Entity drags are unconditionally `place` on any canvas surface
        // — skip the classifier entirely for them.
        if (draggable.kind === "entity" || draggable.kind === "skill") {
          const handler = getBoardDropHandlerRef.current();
          if (!handler) return;
          void Promise.resolve(
            handler({
              screenX,
              screenY,
              payloads: [],
              entities: draggable.entities,
            }),
          ).catch((err) => {
            console.error(
              "[ExplorerDrop] canvas-board entity drop failed",
              err,
            );
            toast.error("Failed to place entity on canvas");
          });
          return;
        }
        if (classification.kind === "noop") return;
        const handler = getBoardDropHandlerRef.current();
        if (!handler) return;
        if (
          classification.kind === "place_chat" &&
          draggable.kind === "chat-session"
        ) {
          void Promise.resolve(
            handler({
              screenX,
              screenY,
              payloads: [],
              chatSession: {
                flowSessionId: classification.flowSessionId,
                name: classification.name,
              },
            }),
          ).catch((err) => {
            console.error("[ExplorerDrop] canvas-board chat drop failed", err);
            toast.error("Failed to place chat on canvas");
          });
          return;
        }
        if (draggable.kind !== "files") return;
        if (classification.kind === "reparent_node") {
          runReparent(
            classification.canvasId,
            classification.nodeIds,
            classification.targetParentSectionId,
          );
          return;
        }
        void invokeCanvasBoardFileDrop(draggable.payloads);
        return;
      }

      // Canvas-section: entity drags from the picker always place into
      // the target section — no classification needed.
      if (
        droppable.surface === "canvas-section" &&
        (draggable.kind === "entity" || draggable.kind === "skill")
      ) {
        const handler = getBoardDropHandlerRef.current();
        if (!handler) return;
        const sectionDestination = droppable.destination as Extract<
          typeof droppable.destination,
          { kind: "canvas_section" }
        >;
        void Promise.resolve(
          handler({
            screenX: 0,
            screenY: 0,
            payloads: [],
            parentSectionId: sectionDestination.sectionId,
            entities: draggable.entities,
          }),
        ).catch((err) => {
          console.error(
            "[ExplorerDrop] canvas-section entity drop failed",
            err,
          );
          toast.error("Failed to place entity into section");
        });
        return;
      }

      // Canvas-section: a `place` classification (some payloads are
      // unplaced canvas files dropped on a section row) or a
      // `place_chat` (chat row dropped on a section) routes to the
      // same board handler as canvas-board drops, with the target
      // section threaded through. The handler computes its own flow
      // drop position from the section's interior origin — the
      // cursor sits in the explorer pane and isn't a useful anchor
      // for nodes that should land inside the section. Reusing the
      // board handler keeps `layoutGridAroundCursor` as the single
      // source of truth for multi-file grid layout.
      if (
        droppable.surface === "canvas-section" &&
        classification.kind === "place_chat" &&
        draggable.kind === "chat-session"
      ) {
        const handler = getBoardDropHandlerRef.current();
        if (!handler) return;
        void Promise.resolve(
          handler({
            screenX: 0,
            screenY: 0,
            payloads: [],
            parentSectionId: classification.parentSectionId ?? undefined,
            chatSession: {
              flowSessionId: classification.flowSessionId,
              name: classification.name,
            },
          }),
        ).catch((err) => {
          console.error("[ExplorerDrop] canvas-section chat drop failed", err);
          toast.error("Failed to place chat into section");
        });
        return;
      }
      if (
        droppable.surface === "canvas-section" &&
        classification.kind === "place" &&
        draggable.kind === "files"
      ) {
        const handler = getBoardDropHandlerRef.current();
        if (!handler) return;
        void Promise.resolve(
          handler({
            // The handler ignores screenX/screenY when
            // `parentSectionId` is set, but the field is required by
            // the type — use the cursor as a safe fallback.
            screenX: 0,
            screenY: 0,
            payloads: draggable.payloads,
            parentSectionId: classification.parentSectionId ?? undefined,
          }),
        ).catch((err) => {
          console.error("[ExplorerDrop] canvas-section place failed", err);
          toast.error("Failed to drop into section");
        });
        return;
      }

      switch (classification.kind) {
        case "noop":
        case "invalid":
        // "place" / "place_chat" only carry semantic meaning when
        // dropped on the canvas board (or a section, handled above).
        // On the canvas-scope explorer body we treat them as no-ops
        // — initial placement happens via the board's drop handler.
        case "place":
        case "place_chat":
          return;
        case "move": {
          if (draggable.kind !== "files") return;
          const moveDest = {
            kind: "workspace" as const,
            workspaceId: classification.workspaceId,
            parentPath: classification.parentPath,
          };
          // Shared-with-me files COPY (the recipient can't relocate a file out
          // of the owner's scope; the backend also rejects the move) — owned
          // files move. Same rule as the Files-rail surface, applied here so the
          // workspace-detail `workspace-folder` surface behaves consistently.
          const sharedPayloads = draggable.payloads.filter(
            (p) => p.sharedSource,
          );
          const ownedPayloads = draggable.payloads.filter(
            (p) => !p.sharedSource,
          );
          const moveTasks: Promise<void>[] = [];
          if (ownedPayloads.length > 0) {
            moveTasks.push(runMoves(ownedPayloads, moveDest));
          }
          if (sharedPayloads.length > 0) {
            moveTasks.push(runCopies(sharedPayloads, moveDest));
          }
          if (moveTasks.length === 0) return;
          void Promise.all(moveTasks).catch((err) => {
            console.error("[ExplorerDrop] move dispatch failed", err);
            toast.error("Failed to move items");
          });
          return;
        }
        case "move_canvas": {
          if (draggable.kind !== "canvas-card") return;
          const ctx = getWorkspaceDetailContextRef.current();
          const canvasMap =
            ctx?.canvasMap ??
            new Map<string, import("@cohesive/api-client").Canvas>();
          void runMoveCanvases(
            draggable.canvasIds,
            classification.workspaceId,
            canvasMap,
          ).catch((err) => {
            console.error("[TransferDrop] move canvas failed", err);
            toast.error("Failed to move canvas");
          });
          return;
        }
        case "move_chat": {
          // Reached when a chat is dropped on the workspace root to un-folder it
          // (chat-folder header drops are handled by the surface block above).
          if (draggable.kind !== "chat-session") return;
          setFlowSessionFolder.mutate({
            flowSessionId: classification.flowSessionId,
            folderId: classification.folderId,
            workspaceId: classification.workspaceId,
          });
          return;
        }
        case "copy":
          if (draggable.kind !== "files") return;
          // Workspace / cross-canvas files dropped on a canvas destination
          // should copy into canvas scope *and* place a node when the board
          // is mounted. `canvas-scope` (explorer body) previously fell
          // through to `runCopies`, which only pinned into the file volume.
          if (droppable.destination.kind === "canvas") {
            void (async () => {
              const placed = await invokeCanvasBoardFileDrop(
                draggable.payloads,
              );
              if (placed) return;
              void runCopies(draggable.payloads, droppable.destination).catch(
                (err) => {
                  console.error("[ExplorerDrop] copy dispatch failed", err);
                  toast.error("Failed to copy items");
                },
              );
            })();
            return;
          }
          void runCopies(draggable.payloads, droppable.destination).catch(
            (err) => {
              console.error("[ExplorerDrop] copy dispatch failed", err);
              toast.error("Failed to copy items");
            },
          );
          return;
        case "section_to_folder":
          if (draggable.kind !== "section") return;
          void runSectionToFolder(draggable.payload, {
            kind: "workspace",
            workspaceId: classification.workspaceId,
            parentPath: classification.parentPath,
          }).catch((err) => {
            console.error("[ExplorerDrop] section dispatch failed", err);
            toast.error("Failed to copy section");
          });
          return;
        case "reparent_node":
          // Same-canvas reorganization. The canvas page owns the
          // geometry math (auto-slot inside the target section, or
          // keep world position when unparenting) and applies it via
          // `canvas.batchUpdateNodeParents`.
          runReparent(
            classification.canvasId,
            classification.nodeIds,
            classification.targetParentSectionId,
          );
          return;
      }
    },
    // `opts.getBoardDropHandler` is read via `getBoardDropHandlerRef`
    // (kept current above) so it deliberately stays out of the dep
    // array — listing the inline `opts` literal here would bust this
    // memo on every parent render.
    [
      runMoves,
      runCopies,
      runSectionToFolder,
      runReparent,
      runMoveCanvases,
      resolveMoves,
      resolveCopies,
      queryClient,
      copyFileRecord.mutateAsync,
      moveFileRecord.mutateAsync,
      deleteFileRecord.mutateAsync,
      updateCanvas.mutateAsync,
      toggleFavorite.mutate,
      updateInboxItem.mutate,
    ],
  );

  const onDragCancel = useCallback((_event: DragCancelEvent) => {
    // No state to reset — overlay unmounts on cancel automatically.
  }, []);

  return { onDragEnd, onDragCancel };
}

/** @deprecated Use `useTransferDispatcher`. */
export function useExplorerDropDispatcher(opts: {
  getBoardDropHandler: () => CanvasBoardDropHandler | null;
}) {
  return useTransferDispatcher({
    getBoardDropHandler: opts.getBoardDropHandler,
    getReparentHandler: () => null,
    getChatAttachHandler: () => null,
    getComposerAttachTarget: () => null,
    getWorkspaceDetailContext: () => null,
  });
}

async function runCopyOperation(
  op: ResolvedCopyOperation,
  copyMutation: ReturnType<typeof useCopyFileRecord>["mutateAsync"],
): Promise<void> {
  await copyMutation({
    fileRecordId: op.sourceFileRecordId,
    payload: op.payload,
  });
}

async function resolveCanvasForMove(
  queryClient: ReturnType<typeof useQueryClient>,
  canvasId: string,
  canvasMap: Map<string, import("@cohesive/api-client").Canvas>,
): Promise<import("@cohesive/api-client").Canvas | null> {
  const fromMap = canvasMap.get(canvasId);
  if (fromMap) return fromMap;

  const cached = queryClient.getQueryData<
    import("@cohesive/api-client").Canvas
  >(["canvas", canvasId]);
  if (cached) return cached;

  const list = queryClient.getQueryData<
    import("@cohesive/api-client").Canvas[]
  >(["canvases"]);
  const fromList = list?.find((canvas) => canvas.id === canvasId);
  if (fromList) return fromList;

  try {
    const canvas = await orgAPI.canvas.getById(canvasId);
    queryClient.setQueryData(["canvas", canvasId], canvas);
    return canvas;
  } catch {
    return null;
  }
}

async function getWorkspaceSourceRecords(
  queryClient: ReturnType<typeof useQueryClient>,
  workspaceId: string,
): Promise<FileRecord[]> {
  const cached = queryClient.getQueryData<FileRecord[]>(
    fileRecordKeys.workspace(workspaceId),
  );
  if (cached) return cached;
  const records = await orgAPI.fileRecords.list({
    class: "workspace",
    workspace_id: workspaceId,
  });
  queryClient.setQueryData(fileRecordKeys.workspace(workspaceId), records);
  return records;
}

async function getCanvasSourceRecords(
  queryClient: ReturnType<typeof useQueryClient>,
  canvasId: string,
): Promise<FileRecord[]> {
  const cached = queryClient.getQueryData<FileRecord[]>(
    fileRecordKeys.canvas(canvasId),
  );
  if (cached) return cached;
  const records = await orgAPI.fileRecords.list({
    class: "canvas",
    canvas_id: canvasId,
  });
  queryClient.setQueryData(fileRecordKeys.canvas(canvasId), records);
  return records;
}

async function getFlowSessionSourceRecords(
  queryClient: ReturnType<typeof useQueryClient>,
  flowSessionId: string,
): Promise<FileRecord[]> {
  const cached = queryClient.getQueryData<FileRecord[]>(
    fileRecordKeys.flowSession(flowSessionId),
  );
  if (cached) return cached;
  const records = await orgAPI.fileRecords.list({
    class: "flow-session",
    flow_session_id: flowSessionId,
  });
  queryClient.setQueryData(fileRecordKeys.flowSession(flowSessionId), records);
  return records;
}

/** Source listings for copy/merge resolution — all dragged scopes. */
async function collectCopySourceRecords(
  queryClient: ReturnType<typeof useQueryClient>,
  payloads: FileRecordCopyPayload[],
): Promise<FileRecord[]> {
  const seen = new Set<string>();
  const records: FileRecord[] = [];
  for (const p of payloads) {
    let scopeKey: string | null = null;
    let fetchRecords: (() => Promise<FileRecord[]>) | null = null;

    if (p.sourceClass === "workspace" && p.sourceWorkspaceId) {
      scopeKey = `workspace:${p.sourceWorkspaceId}`;
      fetchRecords = () =>
        getWorkspaceSourceRecords(queryClient, p.sourceWorkspaceId!);
    } else if (p.sourceClass === "canvas" && p.sourceCanvasId) {
      scopeKey = `canvas:${p.sourceCanvasId}`;
      fetchRecords = () =>
        getCanvasSourceRecords(queryClient, p.sourceCanvasId!);
    } else if (p.sourceClass === "flow-session" && p.sourceFlowSessionId) {
      scopeKey = `flow-session:${p.sourceFlowSessionId}`;
      fetchRecords = () =>
        getFlowSessionSourceRecords(queryClient, p.sourceFlowSessionId!);
    }

    if (!scopeKey || !fetchRecords || seen.has(scopeKey)) continue;
    seen.add(scopeKey);
    try {
      const list = await fetchRecords();
      records.push(...list);
    } catch {
      // best-effort; missing source records only block folder merges,
      // not plain copies.
    }
  }
  return records;
}

async function collectWorkspaceSourceRecords(
  queryClient: ReturnType<typeof useQueryClient>,
  payloads: FileRecordCopyPayload[],
): Promise<FileRecord[]> {
  const seen = new Set<string>();
  const records: FileRecord[] = [];
  for (const p of payloads) {
    if (p.sourceClass !== "workspace" || !p.sourceWorkspaceId) continue;
    if (seen.has(p.sourceWorkspaceId)) continue;
    seen.add(p.sourceWorkspaceId);
    try {
      const list = await getWorkspaceSourceRecords(
        queryClient,
        p.sourceWorkspaceId,
      );
      records.push(...list);
    } catch {
      // best-effort; missing source records only block folder merges,
      // not plain copies.
    }
  }
  return records;
}

async function runMoveOperation(
  op: ResolvedOperation,
  sourceRecordMap: Map<string, FileRecord>,
  allRecords: FileRecord[],
  moveMutation: (vars: {
    fileRecordId: string;
    payload: import("@cohesive/api-client").MoveFileRecordPayload;
  }) => Promise<FileRecord>,
  copyMutation: ReturnType<typeof useCopyFileRecord>["mutateAsync"],
  deleteMutation: ReturnType<typeof useDeleteFileRecord>["mutateAsync"],
): Promise<void> {
  if (op.kind === "move") {
    const sourceRecord = sourceRecordMap.get(op.fileRecordId);
    if (!sourceRecord) {
      await moveMutation({
        fileRecordId: op.fileRecordId,
        payload: op.payload,
      });
      return;
    }
    await moveRecordCascading({
      record: sourceRecord,
      payload: op.payload,
      allRecords,
      moveOne: moveMutation,
    });
    return;
  }
  if (op.kind === "replace_contents") {
    await copyMutation({
      fileRecordId: op.sourceFileRecordId,
      payload: op.payload,
    });
    return;
  }
  await deleteMutation(op.fileRecordId);
}

function patchWorkspaceMoveCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  sourceWorkspaceId: string | undefined,
  movedRecord: FileRecord,
): void {
  const destWorkspaceId = movedRecord.workspace_id;
  if (!destWorkspaceId || movedRecord.class !== "workspace") return;

  queryClient.setQueryData(fileRecordKeys.detail(movedRecord.id), movedRecord);

  if (sourceWorkspaceId && sourceWorkspaceId !== destWorkspaceId) {
    queryClient.setQueryData<FileRecord[]>(
      fileRecordKeys.workspace(sourceWorkspaceId),
      (prev) => (prev ? prev.filter((r) => r.id !== movedRecord.id) : prev),
    );
    queryClient.setQueryData<FileRecord[]>(
      fileRecordKeys.workspace(destWorkspaceId),
      (prev) => {
        if (!prev) return prev;
        if (prev.some((r) => r.id === movedRecord.id)) {
          return prev.map((r) => (r.id === movedRecord.id ? movedRecord : r));
        }
        return [...prev, movedRecord];
      },
    );
    return;
  }

  if (sourceWorkspaceId) {
    queryClient.setQueryData<FileRecord[]>(
      fileRecordKeys.workspace(sourceWorkspaceId),
      (prev) =>
        prev
          ? prev.map((r) => (r.id === movedRecord.id ? movedRecord : r))
          : prev,
    );
  }
}

function invalidateWorkspaceListCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  payloads: FileRecordCopyPayload[],
  destWorkspaceId: string,
): void {
  const workspaceIds = new Set<string>([destWorkspaceId]);
  for (const p of payloads) {
    if (p.sourceClass === "workspace" && p.sourceWorkspaceId) {
      workspaceIds.add(p.sourceWorkspaceId);
    }
  }
  for (const id of workspaceIds) {
    void queryClient.invalidateQueries({
      queryKey: fileRecordKeys.workspace(id),
    });
  }
}

function reportBatchResult({
  verb,
  successes,
  failures,
  total,
  targetLabel,
}: {
  verb: "Moved" | "Copied";
  successes: number;
  failures: number;
  total: number;
  targetLabel: string;
}): void {
  if (failures === 0) {
    toast.success(
      successes === 1
        ? `${verb} 1 item to ${targetLabel}`
        : `${verb} ${successes} items to ${targetLabel}`,
    );
    return;
  }
  if (successes === 0) {
    // Past-tense `verb` ("Moved" / "Copied") reads correctly in the
    // success toast, but "Failed to" needs the base form. Map
    // explicitly so future verbs added to the union force a
    // matching base-form entry instead of a silent grammar bug.
    const baseVerb = verb === "Moved" ? "move" : "copy";
    toast.error(
      total === 1
        ? `Failed to ${baseVerb} item`
        : `Failed to ${baseVerb} ${total} items`,
    );
    return;
  }
  toast.error(`${verb} ${successes} of ${total} items, ${failures} failed`);
}
